import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Grid,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import {
  getAllSofaModels,
  createSofaModel,
  updateSofaModel,
  deleteSofaModel,
  getAllFabrics,
  createFabric,
  updateFabric,
  deleteFabric
} from '../api';

export default function Settings({ user }) {
  const [currentTab, setCurrentTab] = useState('models');
  const [models, setModels] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [modelsRes, fabricsRes] = await Promise.all([
        getAllSofaModels(),
        getAllFabrics()
      ]);
      setModels(modelsRes.data);
      setFabrics(fabricsRes.data);
    } catch (err) {
      setError('Ошибка загрузки данных');
    }
  };

  const handleOpenDialog = (item = null, type) => {
    if (item) {
      setEditingItem({ ...item, type });
      setFormData({ ...item });
    } else {
      setEditingItem({ type });
      if (type === 'model') {
        setFormData({
          name: '',
          description: '',
          fabric_consumption: 0,
          frame_rate: 0,
          sewing_rate: 0,
          upholstery_rate: 0,
          material_cost: 0
        });
      } else {
        setFormData({
          name: '',
          unit: 'м'
        });
      }
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingItem.type === 'model') {
        if (editingItem.id) {
          await updateSofaModel(editingItem.id, formData);
        } else {
          await createSofaModel(formData);
        }
      } else {
        if (editingItem.id) {
          const fabricData = { ...formData };
          delete fabricData.quantity;
          await updateFabric(editingItem.id, fabricData);
        } else {
          await createFabric({ ...formData, quantity: 0 });
        }
      }
      await loadData();
      setDialogOpen(false);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const handleDelete = async (id, type) => {
    if (window.confirm('Удалить запись?')) {
      try {
        if (type === 'model') {
          await deleteSofaModel(id);
        } else {
          await deleteFabric(id);
        }
        await loadData();
      } catch (err) {
        setError('Ошибка удаления');
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Справочники</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Модели диванов" value="models" />
          <Tab label="Ткани" value="fabrics" />
        </Tabs>
      </Paper>

      {currentTab === 'models' && (
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog(null, 'model')}
            sx={{ mb: 2 }}
          >
            Добавить модель
          </Button>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Описание</TableCell>
                  <TableCell>Расход ткани (м)</TableCell>
                  <TableCell>Ставка каркас</TableCell>
                  <TableCell>Ставка пошив</TableCell>
                  <TableCell>Ставка обстрел</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {models.map((model) => (
                  <TableRow key={model.id}>
                    <TableCell><strong>{model.name}</strong></TableCell>
                    <TableCell>{model.description || '-'}</TableCell>
                    <TableCell>{model.fabric_consumption}</TableCell>
                    <TableCell>{model.frame_rate} ₸</TableCell>
                    <TableCell>{model.sewing_rate} ₸</TableCell>
                    <TableCell>{model.upholstery_rate} ₸</TableCell>
                    <TableCell>
                      <IconButton aria-label={`Редактировать модель ${model.name}`} size="small" onClick={() => handleOpenDialog(model, 'model')}>
                        <EditIcon />
                      </IconButton>
                      <IconButton aria-label={`Удалить модель ${model.name}`} size="small" color="error" onClick={() => handleDelete(model.id, 'model')}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {currentTab === 'fabrics' && (
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog(null, 'fabric')}
            sx={{ mb: 2 }}
          >
            Добавить ткань
          </Button>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Количество</TableCell>
                  <TableCell>Единица</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fabrics.map((fabric) => (
                  <TableRow key={fabric.id}>
                    <TableCell><strong>{fabric.name}</strong></TableCell>
                    <TableCell>{fabric.quantity}</TableCell>
                    <TableCell>{fabric.unit}</TableCell>
                    <TableCell>
                      <IconButton aria-label={`Редактировать ткань ${fabric.name}`} size="small" onClick={() => handleOpenDialog(fabric, 'fabric')}>
                        <EditIcon />
                      </IconButton>
                      <IconButton aria-label={`Удалить ткань ${fabric.name}`} size="small" color="error" onClick={() => handleDelete(fabric.id, 'fabric')}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem?.id ? 'Редактировать' : 'Добавить'} {editingItem?.type === 'model' ? 'модель' : 'ткань'}
        </DialogTitle>
        <DialogContent>
          {editingItem?.type === 'model' ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Название модели"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Описание"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Расход ткани (м)"
                  value={formData.fabric_consumption || 0}
                  onChange={(e) => setFormData({ ...formData, fabric_consumption: parseFloat(e.target.value) })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Стоимость материалов (₸)"
                  value={formData.material_cost || 0}
                  onChange={(e) => setFormData({ ...formData, material_cost: parseFloat(e.target.value) })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Ставка каркасника (₸)"
                  value={formData.frame_rate || 0}
                  onChange={(e) => setFormData({ ...formData, frame_rate: parseFloat(e.target.value) })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Ставка швеи (₸)"
                  value={formData.sewing_rate || 0}
                  onChange={(e) => setFormData({ ...formData, sewing_rate: parseFloat(e.target.value) })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Ставка обстрельщика (₸)"
                  value={formData.upholstery_rate || 0}
                  onChange={(e) => setFormData({ ...formData, upholstery_rate: parseFloat(e.target.value) })}
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Название ткани"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Количество" value={formData.quantity || 0} disabled helperText="Остаток изменяется в разделе материалов через приход или расход" />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Единица измерения"
                  value={formData.unit || 'м'}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
